import type { ClientMasterPlanReportingUnit } from "api-types/masterPlans/masterPlansResponses";
import { arrayMove } from "common/utils/arrays";
import { useEffect, useState } from "react";
import DragContext from "ui/components/DragContext";
import cx from "ui/cx";
import InputGroupText from "ui/forms/InputGroupText";
import useDraggableItem from "ui/hooks/useDraggableItem";
import IconHamburger from "ui/icons/IconHamburger";
import styles from "./ReportingUnitsStep.module.scss";

export type ReportingUnitConfig = Omit<ClientMasterPlanReportingUnit, "createdAt">;

interface ReportingUnitsStepProps {
  reportingUnits: ReportingUnitConfig[];
  onReportingUnitsChange: (units: ReportingUnitConfig[]) => void;
}

function hasDuplicateName(units: ReportingUnitConfig[], currentIndex: number, name: string): boolean {
  const trimmedName = name.trim().toLowerCase();
  return units.some((unit, index) => index !== currentIndex && unit.name.trim().toLowerCase() === trimmedName);
}

interface ReportingUnitRowProps {
  unit: ReportingUnitConfig;
  index: number;
  allUnits: ReportingUnitConfig[];
  onNameChange: (index: number, name: string) => void;
  onExternalIdChange: (index: number, externalId: string) => void;
  onMoveRow: (dragIndex: number, hoverIndex: number) => void;
  onDropRow: () => void;
}

function ReportingUnitRow({
  unit,
  index,
  allUnits,
  onNameChange,
  onExternalIdChange,
  onMoveRow,
  onDropRow,
}: ReportingUnitRowProps) {
  const { dragRef, dropRef, isDragging, isOver } = useDraggableItem<{ index: number }>({
    dragItem: onMoveRow,
    dropItem: onDropRow,
    index,
    dragItemType: "reporting-unit",
  });

  return (
    <div ref={dropRef} className={cx(styles.row, isDragging && styles.dragging, isOver && styles.dropTarget)}>
      <div ref={dragRef} className={styles.dragHandle}>
        <IconHamburger size={8} color="stone" />
      </div>
      <div className={styles.rowContent}>
        <InputGroupText
          type="text"
          labelText="Name"
          inputValue={unit.name}
          onChange={(value) => onNameChange(index, value)}
          placeholder={`Tag ${unit.reportingUnitId}`}
          dataTestId={`reporting-unit-name-${unit.reportingUnitId}`}
          required
          validate={(value) => {
            if (!value || value.toString().trim().length === 0) {
              return "Name is required";
            }
            if (hasDuplicateName(allUnits, index, value.toString())) {
              return "Name must be unique";
            }
            return undefined;
          }}
        />
        <InputGroupText
          type="text"
          labelText="External ID"
          inputValue={unit.externalId ?? ""}
          onChange={(value) => onExternalIdChange(index, value)}
          placeholder="Optional"
        />
      </div>
    </div>
  );
}

export default function ReportingUnitsStep({ reportingUnits, onReportingUnitsChange }: ReportingUnitsStepProps) {
  // Local state for drag operations - only propagate to parent on drop
  const [localUnits, setLocalUnits] = useState(reportingUnits);

  // Sync local state when parent state changes (e.g., initial load)
  useEffect(() => {
    setLocalUnits(reportingUnits);
  }, [reportingUnits]);

  const handleNameChange = (index: number, name: string) => {
    const updated = [...localUnits];
    updated[index] = { ...updated[index], name };
    setLocalUnits(updated);
    onReportingUnitsChange(updated);
  };

  const handleExternalIdChange = (index: number, externalId: string) => {
    const updated = [...localUnits];
    updated[index] = { ...updated[index], externalId };
    setLocalUnits(updated);
    onReportingUnitsChange(updated);
  };

  // Called on hover - update local state for visual feedback
  const handleMoveRow = (dragIndex: number, hoverIndex: number) => {
    setLocalUnits((prev) => {
      const reordered = [...prev];
      arrayMove(reordered, dragIndex, hoverIndex);
      return reordered;
    });
  };

  // Called on drop - propagate final order to parent with updated sortIndex
  const handleDropRow = () => {
    const withUpdatedIndices = localUnits.map((unit, idx) => ({
      ...unit,
      sortIndex: idx,
    }));
    onReportingUnitsChange(withUpdatedIndices);
  };

  return (
    <DragContext>
      <div className={styles.container}>
        <div className={styles.list}>
          {localUnits.map((unit, index) => (
            <ReportingUnitRow
              key={unit.reportingUnitId}
              unit={unit}
              index={index}
              allUnits={localUnits}
              onNameChange={handleNameChange}
              onExternalIdChange={handleExternalIdChange}
              onMoveRow={handleMoveRow}
              onDropRow={handleDropRow}
            />
          ))}
        </div>
      </div>
    </DragContext>
  );
}
